/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-2016 Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import { withTracking } from 'react-tracker';
import { connect } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import Password from '../../../../../components/Form/Input/Password';
import Auth from '../Auth';
import Callout from '../../../../../components/Callout';
import Link from '../../../../../components/Link';
import Form from '../../../../../components/Form';
import Text from '../../../../../components/Form/Input/Text';
import Checkbox from '../../../../../components/Form/Input/Checkbox';
// import Terms from './Terms';
import { RUNTIME_MODAL } from '../../../../../constants';
import { setRuntimeVariable } from '../../../../../actions/runtime';
import * as SessionActions from '../../../../../actions/SessionActions';
import history from '../../../../../history';
import { sites } from '../../../../../configPublic';
import { getTrackingEvent } from '../../../../../tracking/events';
import {
  isValidPassword,
  PASSWORD_HELP_TEXT,
  PASSWORD_ERROR_TEXT,
} from '../../../../../core/passwordPolicy';

const messages = defineMessages({
  login: {
    id: 'auth.registr.login',
    defaultMessage: 'LOGIN',
    description: 'Login Link',
  },
  signup: {
    id: 'auth.registr.signup',
    defaultMessage: 'SIGN UP',
    description: 'Sign Up Link',
  },
  member: {
    id: 'auth.registr.member',
    defaultMessage: 'Already member on mySKP?',
    description: 'Already member',
  },
  memberLogin: {
    id: 'auth.registr.memberLogin',
    defaultMessage: 'Log in',
    description: 'Log in Link',
  },
  termsMessage: {
    id: 'auth.registr.termsMessage',
    defaultMessage: 'By clicking Sign Up, you agree to our',
    description: 'Terms Message',
  },
  termsLink: {
    id: 'auth.registr.termsLink',
    defaultMessage: 'Terms',
    description: 'Terms Link',
  },
  dataPolicyMessage: {
    id: 'auth.registr.dataPolicyMessage',
    defaultMessage: 'and that you have read our',
    description: 'Data Policy Message',
  },
  dataPolicyLink: {
    id: 'auth.registr.dataPolicyLink',
    defaultMessage: 'Data Policy',
    description: 'Data Policy Link',
  },
  cookieMessage: {
    id: 'auth.registr.cookieMessage',
    defaultMessage: 'including our',
    description: 'Cookie Message',
  },
  cookieLink: {
    id: 'auth.registr.cookieLink',
    defaultMessage: 'Cookie Use',
    description: 'Cookie Link',
  },
  sendingEmailMessage: {
    id: 'auth.registr.sendingEmailMessage',
    defaultMessage:
      'By Submitting this form I acknowledge that Schwarzkopf Professional will be sending me information regarding my clubSKP account to the email address I have registered this account with.*',
    description: 'Sending Email Message',
  },
  subscribeMessage: {
    id: 'auth.registr.subscribeMessage',
    defaultMessage:
      'Yes, I would to receive commercial electronic information and offers from { company }, (“SKP”). I may later unsubscribe. [{ company }. {address}].',
    description: 'Subscribe Message',
  },

  firstName: {
    id: 'auth.registr.firstName',
    defaultMessage: 'First Name',
    description: 'First Name Label',
  },
  lastName: {
    id: 'auth.registr.lastName',
    defaultMessage: 'Last Name',
    description: 'Last Name Label',
  },
  emailAddress: {
    id: 'auth.registr.emailAddress',
    defaultMessage: 'Email Address',
    description: 'Email Address Label',
  },
  password: {
    id: 'auth.registr.password',
    defaultMessage: 'Password',
    description: 'Password Label',
  },
  checkboxError: {
    id: 'auth.registr.checkboxError',
    defaultMessage: 'You must agree to terms.',
    description: 'You must agree to terms message',
  },

  submittingBtn: {
    id: 'auth.registr.submittingBtn',
    defaultMessage: '...submitting',
    description: '...submitting button text',
  },
  signupBtn: {
    id: 'auth.registr.signupBtn',
    defaultMessage: 'SIGN UP FOR MYSKP',
    description: 'SIGN UP FOR MYSKP button text',
  },

  agreeText1: {
    id: 'auth.registr.agreeText1',
    defaultMessage: 'I have read and agree to the',
    description: 'agreeText1',
  },
  agreeText2: {
    id: 'auth.registr.agreeText2',
    defaultMessage: 'Terms of Use and Privacy Policy',
    description: 'agreeText2',
  },
  agreeText3: {
    id: 'auth.registr.agreeText3',
    defaultMessage: 'and',
    description: 'agreeText3',
  },
  agreeText4: {
    id: 'auth.registr.agreeText4',
    defaultMessage: 'MYSKP Additional Terms Of Use And Privacy Supplement',
    description: 'agreeText4',
  },
});

class Register extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    showTerms: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    track: PropTypes.func.isRequired,
  };

  static contextTypes = {
    url: PropTypes.object.isRequired,
    routeParams: PropTypes.object.isRequired,
    fetch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
  };

  constructor(...args) {
    super(...args);
    this.captcha = null;
    this.state = {
      submitting: false,
      submitSuccess: false,
      submitError: false,
      value: {},
    };
  }

  componentDidMount() {
    const { session } = this.props; //eslint-disable-line
    const { url, routeParams } = this.context;
    if (session.USER_DATA) {
      history.replace(url.getUrl('home', routeParams));
    }
  }

  onCaptchaChange(value) {
    if (value === null) {
      this.setState({
        submitError: true,
        submitErrorMessage: 'Captcha expired. Please try again.',
      });
    } else {
      this.postData(value);
    }
    this.captcha.reset();
  }

  async postData(recaptchaResponse) {
    const { siteId } = this.context.routeParams;
    const { dispatch } = this.props;
    this.setState({
      submitting: true,
      submitSuccess: false,
      submitError: false,
    });

    const resp = await this.context.fetch(`/${siteId}/auth/register/user`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        Object.assign(this.state.value, {
          'g-recaptcha-response': recaptchaResponse,
        }),
      ),
      credentials: 'include',
    });
    const data = await resp.json();
    if (!data || resp.status !== 200 || data.responseCode === 1) {
      this.setState({
        submitError: true,
        submitErrorMessage: data.responseDesc,
        submitting: false,
      });
    } else {
      this.props.track({ type: 'SIGNUP_USER_SUCCESS', siteId, data: null });
      dispatch(SessionActions.setMeData(data.me));
      this.setState(
        {
          submitting: false,
          submitError: false,
          submitSuccess: true,
          value: {},
        },
        () => history.push(`/${siteId}/auth/salon`),
      );
    }
  }

  render() {
    const { url, routeParams, intl } = this.context;
    const callout = this.state.submitError ? (
      <Callout size="small" type="warning">
        {this.state.submitErrorMessage}
      </Callout>
    ) : null;

    const Terms = sites[routeParams.siteId].terms(intl).generalTerms.component;
    const TermsLabel = sites[routeParams.siteId].terms(intl).generalTerms.label;
    const TermsProps = sites[routeParams.siteId].terms(intl).generalTerms.props;

    const TermsAdditional = sites[routeParams.siteId].terms(intl)
      .additionalTerms.component;
    const TermsAdditionalLabel = sites[routeParams.siteId].terms(intl)
      .additionalTerms.label;
    const TermsAdditionalProps = sites[routeParams.siteId].terms(intl)
      .additionalTerms.props;

    return (
      <Auth>
        <div className="auth__form">
          <div className="flex-center auth__links">
            <Link
              className="link link--medium"
              to={url.getUrl('auth-index', routeParams)}
              activeClassName="is-active"
            >
              <FormattedMessage {...messages.login} />
            </Link>
            <Link
              className="link link--medium"
              to={url.getUrl('auth-register', routeParams)}
              activeClassName="is-active"
            >
              <FormattedMessage {...messages.signup} />
            </Link>
          </div>
          <div className="auth__main">
            {callout}

            <Form
              title={this.props.title}
              // onInvalidSubmit={this.onInvalidSubmit.bind(this)}
              // onValidSubmit={this.onValidSubmit.bind(this)}
              onValidSubmit={e => {
                this.setState({ value: e.value }, () => this.captcha.execute());
              }}
              // onSubmit={this.onSubmit.bind(this)}
              // onChange={this.onChange.bind(this)}
              // onCancel={this.onCancel.bind(this)}
              // onInit={this.onInit.bind(this)}
              value={this.state.value}
              isSubmitting={this.state.submitting}
              submitButtonLabel={
                this.state.submitting
                  ? intl.formatMessage(messages.submittingBtn)
                  : intl.formatMessage(messages.signupBtn)
              }
            >
              {/* <Text label="Salon Name" helpText="" errorText="" name="salon" required /> */}
              <Text
                label={intl.formatMessage(messages.firstName)}
                helpText=""
                errorText=""
                name="firstname"
                required
              />
              <Text
                label={intl.formatMessage(messages.lastName)}
                helpText=""
                errorText=""
                name="lastname"
                required
              />
              <Text
                label={intl.formatMessage(messages.emailAddress)}
                helpText=""
                errorText="Valid email address required."
                name="email"
                validators="isEmail"
                required
              />
              <Password
                label={intl.formatMessage(messages.password)}
                helpText={PASSWORD_HELP_TEXT}
                errorText={PASSWORD_ERROR_TEXT}
                name="password"
                validators={val => isValidPassword(`${val}`)}
                required
              />
              <Checkbox
                label=""
                helpText=""
                errorText={intl.formatMessage(messages.checkboxError)}
                validators={val => val === true}
                name="_agree"
                // required
              >
                <span>
                  <FormattedMessage {...messages.agreeText1} />{' '}
                  <a
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      this.props.showTerms({
                        title: TermsLabel,
                        body: <Terms {...TermsProps} />,
                      });
                    }}
                  >
                    <FormattedMessage {...messages.agreeText2} />
                  </a>
                  , <FormattedMessage {...messages.agreeText3} />{' '}
                  <a
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      this.props.showTerms({
                        title: TermsAdditionalLabel,
                        body: <TermsAdditional {...TermsAdditionalProps} />,
                      });
                    }}
                  >
                    <FormattedMessage {...messages.agreeText4} />
                  </a>
                </span>
              </Checkbox>
              <div className="auth__agree pt20">
                <Checkbox
                  label=""
                  helpText=""
                  errorText="You must agree subscribe."
                  validators={val => val === true}
                  name="_subscribe"
                  // required
                >
                  <span>
                    <FormattedMessage
                      {...messages.subscribeMessage}
                      values={{
                        company: sites[routeParams.siteId].company,
                        address: `${
                          sites[routeParams.siteId].contact.address.str1
                        }, ${sites[routeParams.siteId].contact.address.city}, ${
                          sites[routeParams.siteId].contact.address.state
                        } ${
                          sites[routeParams.siteId].contact.address.postal
                        }, ${sites[routeParams.siteId].contact.phone}`,
                      }}
                    />
                  </span>
                </Checkbox>
              </div>
              <ReCAPTCHA
                ref={el => {
                  this.captcha = el;
                }}
                size="invisible"
                sitekey="6LehjCYUAAAAAEEmIEB3jHfBUxfS_J1s48vic39f"
                onChange={this.onCaptchaChange.bind(this)} // eslint-disable-line
              />
            </Form>
          </div>

          <p className="auth__help">
            <FormattedMessage {...messages.member} />{' '}
            <Link
              className="auth__link"
              to={url.getUrl('auth-index', routeParams)}
            >
              <FormattedMessage {...messages.memberLogin} />
            </Link>
          </p>
        </div>
      </Auth>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  showTerms: ({ title, body }) => {
    dispatch(
      setRuntimeVariable({
        name: RUNTIME_MODAL,
        value: {
          type: 'full',
          payload: {
            title,
            body,
          },
        },
      }),
    );
  },
  dispatch,
});

const mapTrackingToProps = trackEvent => ({
  track: ({ type, siteId, data }) => {
    trackEvent(getTrackingEvent({ type, siteId, data }));
  },
});

// export default Login;
export default withTracking(mapTrackingToProps)(
  connect(state => ({ session: state.session }), mapDispatchToProps)(Register),
);



// WEBPACK FOOTER //
// ./src/routes/client/site/auth/register/Register.js